import { useState } from 'react';
import type { ReactElement } from 'react';
import { istGueltigeEmailAdresse } from '@mcikm-vote/shared';

import { useEmailVersandStarten, useEmailVersandStatus } from '../admin/emailVersandApi';
import { BestaetigungsDialog } from './BestaetigungsDialog';
import { Fehlermeldung } from './Fehlermeldung';
import { Feld } from './Feld';
import { Knopf } from './Knopf';

/**
 * Per E-Mail versenden (ÄA-7).
 *
 * Steht auf der Tokenverwaltung (`TokensSeite.tsx`) — dort, wo auch Tokens erzeugt und verwaltet
 * werden — und **nicht** hinter dem „Tokens anzeigen"-Vorbehalt der Token-Ausgabe: Der Versand
 * setzt nicht voraus, dass der Administrator die Tokens auf dem Bildschirm sieht — die Zuordnung
 * zwischen Adresse und Token entsteht serverseitig und bleibt dort auch für den Administrator
 * unsichtbar.
 */
export function EmailVersandAbschnitt({
  veranstaltungId,
}: {
  veranstaltungId: string;
}): ReactElement {
  const [eingabe, setEingabe] = useState('');
  const [bestaetigen, setBestaetigen] = useState(false);

  const status = useEmailVersandStatus(veranstaltungId, veranstaltungId !== '');
  const starten = useEmailVersandStarten(veranstaltungId);

  const zeilen = eingabe
    .split(/[\n,;]+/)
    .map((zeile) => zeile.trim())
    .filter((zeile) => zeile !== '');
  const gueltig = [...new Set(zeilen.filter((zeile) => istGueltigeEmailAdresse(zeile)))];
  const ungueltig = zeilen.filter((zeile) => !istGueltigeEmailAdresse(zeile));

  const laeuft = status.data?.laufend === true;

  return (
    <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
      <h2 className="mb-1 text-lg font-medium">Per E-Mail versenden</h2>
      <p className="text-text-gedaempft mt-1 text-sm">
        Eine Adresse je Zeile. Jede Adresse erhält eine eigene Zugangskarte mit einem eigenen Token
        als PDF-Anhang. Es wird nirgends vermerkt, welcher Token an welche Adresse ging. Absender
        und Mailtext werden unter „E-Mail-Versand" in der Verwaltung gepflegt.
      </p>

      <div className="mt-3">
        <Feld
          name="email-empfaenger"
          beschriftung="Empfängeradressen"
          wert={eingabe}
          mehrzeilig
          zeilen={6}
          onAendern={setEingabe}
        />
      </div>

      {zeilen.length > 0 && (
        <p className="text-text-gedaempft mt-1 text-xs">
          {gueltig.length} gültige Adresse(n)
          {ungueltig.length > 0 &&
            `, ${ungueltig.length} ungültige Zeile(n): ${ungueltig.join(', ')}`}
        </p>
      )}

      {starten.isError && (
        <div className="mt-2">
          <Fehlermeldung>{starten.error.message}</Fehlermeldung>
        </div>
      )}

      {!bestaetigen ? (
        <div className="mt-3">
          <Knopf
            type="button"
            disabled={gueltig.length === 0 || laeuft}
            onClick={() => setBestaetigen(true)}
          >
            Versenden
          </Knopf>
        </div>
      ) : (
        <div className="mt-3">
          <BestaetigungsDialog
            titel="Zugangskarten per E-Mail versenden"
            beschreibung={
              <>
                {gueltig.length} Zugangskarte(n) werden versendet. Reicht der Vorrat unbenutzter
                Tokens nicht aus, wird nichts versendet. Der Vorgang läuft im Hintergrund.
              </>
            }
            bestaetigungstext="Jetzt versenden"
            laeuft={starten.isPending}
            onBestaetigen={() => {
              starten.mutate(gueltig, { onSuccess: () => setBestaetigen(false) });
            }}
            onAbbrechen={() => setBestaetigen(false)}
          />
        </div>
      )}

      {status.data !== undefined && status.data.gesamt > 0 && (
        <div className="border-rand mt-4 rounded-md border p-3 text-sm">
          <p className="font-medium">
            {laeuft ? 'Versand läuft …' : 'Letzter Versand abgeschlossen.'} {status.data.erledigt}{' '}
            von {status.data.gesamt} verarbeitet, {status.data.erfolgreich} erfolgreich
            {status.data.fehlgeschlageneAdressen.length > 0 &&
              `, ${status.data.fehlgeschlageneAdressen.length} fehlgeschlagen`}
            .
          </p>
          <div className="bg-flaeche-gedaempft mt-2 h-2 overflow-hidden rounded-full">
            <div
              className="bg-akzent h-full transition-all"
              style={{ width: `${Math.round((status.data.erledigt / status.data.gesamt) * 100)}%` }}
            />
          </div>

          {!laeuft && status.data.fehlgeschlageneAdressen.length > 0 && (
            <div className="mt-2">
              <p className="text-text-gedaempft text-xs">
                Fehlgeschlagene Adressen: {status.data.fehlgeschlageneAdressen.join(', ')}
              </p>
              <Knopf
                type="button"
                art="sekundaer"
                className="mt-2 min-h-0 px-3 py-1 text-sm"
                onClick={() => setEingabe(status.data?.fehlgeschlageneAdressen.join('\n') ?? '')}
              >
                Fehlgeschlagene erneut in das Feld übernehmen
              </Knopf>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
